/**
 * WI-B-05 — session-stale signal. A single app-wide flag: has the current
 * session's credential gone stale (a bare 401 came back from the coordinator)?
 *
 * This is the seam between the outbox dispatcher (which OBSERVES the 401 on the
 * send path) and the shell (which PINS the A7 "you've been signed out" banner
 * and offers "Sign back in"). Deliberately tiny and dependency-free so both the
 * transport layer and the UI layer can touch it without importing each other.
 *
 * The state is anchored on a single globalThis-scoped cell so every importer
 * shares ONE flag + ONE listener set, even if the module graph is instantiated
 * more than once (bundler code-splitting, or a test runner's ESM transform
 * producing duplicate module copies). Without this anchor the dispatcher could
 * mark stale on one copy while the shell listens on another and the A7 banner
 * would never pin — the exact split that hid this wiring in the DOM suite.
 *
 * Invariants (PIN-B-4):
 *  - Going stale NEVER touches the outbox — captures stay queued and new
 *    captures keep recording. This flag is a UI signal, not a gate.
 *  - Clearing it is what "signed back in" does; the dispatcher then drains the
 *    held queue normally (no loss/dupe/reorder — those properties live in the
 *    outbox spine, untouched here).
 */

type StaleListener = (stale: boolean) => void;

interface StaleCell {
  stale: boolean;
  listeners: Set<StaleListener>;
}

const CELL_KEY = "__mark_session_stale_cell__";

/** The one shared cell, anchored on globalThis so all module copies share it. */
function cell(): StaleCell {
  const g = globalThis as unknown as Record<string, StaleCell | undefined>;
  let c = g[CELL_KEY];
  if (!c) {
    c = { stale: false, listeners: new Set<StaleListener>() };
    g[CELL_KEY] = c;
  }
  return c;
}

/** True once a bare 401 has been observed on the send path and not yet cleared
 * by a fresh sign-in. */
export function isSessionStale(): boolean {
  return cell().stale;
}

/** Mark the session stale (called by the dispatcher on an auth-stale reply).
 * Idempotent: re-marking an already-stale session notifies no one, so a burst
 * of 401s from several in-flight groups pins the banner once, not N times. */
export function markSessionStale(): void {
  const c = cell();
  if (c.stale) return;
  c.stale = true;
  for (const l of c.listeners) l(true);
}

/** Clear the stale flag — what a successful re-authentication does. Idempotent
 * in the same way. After this the dispatcher's next tick drains the held queue. */
export function clearSessionStale(): void {
  const c = cell();
  if (!c.stale) return;
  c.stale = false;
  for (const l of c.listeners) l(false);
}

/** Subscribe to stale-state changes (the A7 banner uses this to pin/unpin).
 * Immediately invokes the listener with the CURRENT value so a subscriber that
 * mounts AFTER the session already went stale reconciles at once (edge + level,
 * not edge-only) — this is what lets the A7 banner pin even when the 401 landed
 * before the shell's effect ran. Returns an unsubscribe function. */
export function onSessionStaleChange(listener: StaleListener): () => void {
  const c = cell();
  c.listeners.add(listener);
  listener(c.stale);
  return () => c.listeners.delete(listener);
}

/** Test-only: reset module state so each test starts fresh. */
export function _resetSessionStaleForTests(): void {
  const c = cell();
  c.stale = false;
  c.listeners.clear();
}
